import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE, listCrmJobs, listCrmCandidates, updateCrmCandidate } from '../services/api';
import { useT } from '../i18n/LangContext';
import AppNav from '../components/AppNav';

function getStatusMeta(t) {
  return {
    published: { label: t('crmStatusPublished'), color: '#10b981', bg: '#d1fae5', border: '#6ee7b7', step: 3 },
    draft:     { label: t('crmStatusInReview'),  color: '#5b6af0', bg: '#e0e7ff', border: '#a5b4fc', step: 2 },
    pending:   { label: t('crmStatusPending'),   color: '#f59e0b', bg: '#fef3c7', border: '#fcd34d', step: 1 },
  };
}

function DonutChart({ published, draft, pending, totalLabel = 'total' }) {
  const total = published + draft + pending || 1;
  const data = [
    { value: published, color: '#10b981' },
    { value: draft,     color: '#5b6af0' },
    { value: pending,   color: '#f59e0b' },
  ];

  const r = 52;
  const cx = 70;
  const cy = 70;
  const circumference = 2 * Math.PI * r;
  let offset = 0;

  const slices = data.map((d) => {
    const dash = (d.value / total) * circumference;
    const gap = circumference - dash;
    const slice = { ...d, dash, gap, offset };
    offset += dash;
    return slice;
  });

  return (
    <svg width="140" height="140" viewBox="0 0 140 140">
      {/* background ring */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f0f1f5" strokeWidth="18" />
      {slices.map((sl, i) => (
        <circle
          key={i}
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={sl.color}
          strokeWidth="18"
          strokeDasharray={`${sl.dash} ${sl.gap}`}
          strokeDashoffset={-sl.offset + circumference / 4}
          style={{ transition: 'stroke-dasharray 0.5s ease' }}
        />
      ))}
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize="22" fontWeight="800" fill="#1a1a2e">
        {published + draft + pending}
      </text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize="11" fill="#888">
        {totalLabel}
      </text>
    </svg>
  );
}

const STAGE_COLORS = {
  applied:   { color: '#5b6af0', bg: '#eef2ff' },
  screening: { color: '#f59e0b', bg: '#fef3c7' },
  interview: { color: '#8b5cf6', bg: '#f3e8ff' },
  offer:     { color: '#10b981', bg: '#d1fae5' },
  hired:     { color: '#059669', bg: '#a7f3d0' },
  rejected:  { color: '#ef4444', bg: '#fee2e2' },
};
const STAGES = ['applied', 'screening', 'interview', 'offer', 'hired', 'rejected'];

function CandidateModal({ candidate, onClose, onStageChange }) {
  const raw = candidate.raw_data || {};
  const fileFields = Object.entries(raw).filter(([, v]) => typeof v === 'string' && v.startsWith('/uploads/'));
  const textFields = Object.entries(raw).filter(([, v]) => typeof v === 'string' && !v.startsWith('/uploads/') && v.trim());

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:'16px', width:'100%', maxWidth:'580px', maxHeight:'85vh', overflowY:'auto', padding:'28px', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'20px' }}>
          <div>
            <h2 style={{ margin:0, fontSize:'20px', fontWeight:800, color:'#1a1a2e' }}>{candidate.full_name || 'Applicant'}</h2>
            <p style={{ margin:'4px 0 0', fontSize:'13px', color:'#888' }}>{new Date(candidate.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}</p>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'1px solid #e5e7eb', borderRadius:'8px', padding:'6px 12px', cursor:'pointer', fontSize:'13px', color:'#666' }}>✕ Close</button>
        </div>

        {/* Key info */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'20px' }}>
          {candidate.email && <div style={{ background:'#f8fafc', borderRadius:'8px', padding:'12px' }}><p style={{ margin:0, fontSize:'11px', color:'#888', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>Email</p><p style={{ margin:'4px 0 0', fontSize:'14px', color:'#1a1a2e', wordBreak:'break-all' }}>{candidate.email}</p></div>}
          {candidate.phone && <div style={{ background:'#f8fafc', borderRadius:'8px', padding:'12px' }}><p style={{ margin:0, fontSize:'11px', color:'#888', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>Phone</p><p style={{ margin:'4px 0 0', fontSize:'14px', color:'#1a1a2e' }}>{candidate.phone}</p></div>}
        </div>

        {/* Stage selector */}
        <div style={{ marginBottom:'20px' }}>
          <p style={{ margin:'0 0 8px', fontSize:'12px', color:'#888', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>Stage</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
            {STAGES.map((st) => {
              const active = candidate.stage === st;
              const c = STAGE_COLORS[st];
              return (
                <button key={st} onClick={() => onStageChange(candidate.id, st)} style={{ padding:'5px 14px', borderRadius:'20px', border:`1px solid ${active ? c.color : '#e5e7eb'}`, background: active ? c.bg : '#fff', color: active ? c.color : '#666', fontSize:'12px', fontWeight: active ? 700 : 500, cursor:'pointer', textTransform:'capitalize' }}>
                  {st}
                </button>
              );
            })}
          </div>
        </div>

        {/* CV / file attachments */}
        {fileFields.length > 0 && (
          <div style={{ marginBottom:'20px' }}>
            <p style={{ margin:'0 0 8px', fontSize:'12px', color:'#888', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>Files / CV</p>
            {fileFields.map(([key, url]) => (
              <a key={key} href={`${API_BASE}${url}`} target="_blank" rel="noreferrer" style={{ display:'block', padding:'10px 14px', background:'#eef2ff', borderRadius:'8px', color:'#5b6af0', fontSize:'13px', fontWeight:600, textDecoration:'none', marginBottom:'6px' }}>
                📎 {key} — Download
              </a>
            ))}
          </div>
        )}

        {/* All form fields */}
        {textFields.length > 0 && (
          <div>
            <p style={{ margin:'0 0 10px', fontSize:'12px', color:'#888', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>Form Data</p>
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {textFields.map(([key, val]) => (
                <div key={key} style={{ background:'#f8fafc', borderRadius:'8px', padding:'10px 14px' }}>
                  <span style={{ fontSize:'11px', color:'#aaa', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>{key}</span>
                  <p style={{ margin:'3px 0 0', fontSize:'14px', color:'#1a1a2e', wordBreak:'break-word' }}>{val}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CrmPage() {
  const navigate = useNavigate();
  const { t } = useT();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [stageFilter, setStageFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    listCrmJobs()
      .then((res) => setJobs(res.jobs || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setCandidatesLoading(true);
    listCrmCandidates({ pageSize: 50 })
      .then((res) => setCandidates(res.candidates || []))
      .catch(() => {})
      .finally(() => setCandidatesLoading(false));
  }, []);

  async function handleStageChange(candidateId, stage) {
    try {
      await updateCrmCandidate({ candidateId, payload: { stage } });
      setCandidates((prev) => prev.map((c) => c.id === candidateId ? { ...c, stage } : c));
      setSelectedCandidate((prev) => prev && prev.id === candidateId ? { ...prev, stage } : prev);
    } catch (_) {}
  }

  const filteredCandidates = useMemo(() => {
    let list = candidates;
    if (stageFilter) list = list.filter((c) => c.stage === stageFilter);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      list = list.filter((c) => (c.full_name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q));
    }
    return list;
  }, [candidates, stageFilter, searchText]);

  const STATUS_META = useMemo(() => getStatusMeta(t), [t]);
  const STEPS = useMemo(() => [t('crmStepSubmitted'), t('crmStepReviewing'), t('crmStepLive')], [t]);

  const stats = useMemo(() => ({
    published: jobs.filter((j) => j.page_status === 'published').length,
    draft:     jobs.filter((j) => j.page_status === 'draft').length,
    pending:   jobs.filter((j) => !j.page_status || j.page_status === 'pending').length,
    total:     jobs.length,
  }), [jobs]);

  const userEmail = localStorage.getItem('userEmail') || '';

  return (
    <div style={s.page}>
      <AppNav activePage="crm" />

      <div style={s.shell}>
        {/* HEADER */}
        <div style={s.header}>
          <div>
            <h1 style={s.h1}>{t('crmMyDashboard')}</h1>
            {userEmail && <p style={s.subtitle}>{userEmail}</p>}
          </div>
          <button style={s.ctaBtn} onClick={() => navigate('/')}>
            {t('crmNewRequest')}
          </button>
        </div>

        {error && <p style={{ color: '#e74c3c', marginBottom: '16px' }}>{error}</p>}

        {loading ? (
          <div style={s.loadingCard}>
            <div style={s.spinner} />
            <p style={{ color: '#888', marginTop: '12px' }}>{t('crmLoadingPages')}</p>
          </div>
        ) : jobs.length === 0 ? (
          /* EMPTY STATE */
          <div style={s.emptyCard}>
            <div style={s.emptyIcon}>📋</div>
            <h2 style={s.emptyTitle}>{t('crmNoRequests')}</h2>
            <p style={s.emptyText}>{t('crmNoRequestsDesc')}</p>
            <button style={s.ctaBtn} onClick={() => navigate('/')}>{t('crmSubmitFirstRequest')}</button>
          </div>
        ) : (
          <>
            {/* STATS + CHART ROW */}
            <div style={s.topGrid}>
              {/* stat cards */}
              <div style={s.statsCol}>
                {[
                  { label: t('crmTotalRequests'),   value: stats.total,     color: '#1a1a2e', bg: '#f4f5f7', border: '#e5e7eb' },
                  { label: t('crmStatusPublished'), value: stats.published, color: '#10b981', bg: '#d1fae5', border: '#6ee7b7' },
                  { label: t('crmStatusInReview'),  value: stats.draft,     color: '#5b6af0', bg: '#e0e7ff', border: '#a5b4fc' },
                  { label: t('crmStatusPending'),   value: stats.pending,   color: '#f59e0b', bg: '#fef3c7', border: '#fcd34d' },
                ].map((st) => (
                  <div key={st.label} style={{ ...s.statCard, background: st.bg, border: `1px solid ${st.border}` }}>
                    <span style={{ ...s.statVal, color: st.color }}>{st.value}</span>
                    <span style={s.statLabel}>{st.label}</span>
                  </div>
                ))}
              </div>

              {/* donut chart */}
              <div style={s.chartCard}>
                <p style={s.chartTitle}>{t('crmStatusBreakdown')}</p>
                <DonutChart published={stats.published} draft={stats.draft} pending={stats.pending} totalLabel={t('crmChartTotal')} />
                <div style={s.legend}>
                  {[
                    { label: t('crmStatusPublished'), color: '#10b981' },
                    { label: t('crmStatusInReview'),  color: '#5b6af0' },
                    { label: t('crmStatusPending'),   color: '#f59e0b' },
                  ].map((l) => (
                    <div key={l.label} style={s.legendItem}>
                      <span style={{ ...s.legendDot, background: l.color }} />
                      <span style={s.legendLabel}>{l.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* JOB CARDS */}
            <h2 style={s.sectionTitle}>{t('crmMyLandingPages')}</h2>
            <div style={s.cardGrid}>
              {jobs.map((job) => {
                const pageStatus = job.page_status || (job.page_id ? 'pending' : 'pending');
                const meta = STATUS_META[pageStatus] || STATUS_META.pending;
                const step = meta.step;

                return (
                  <div key={job.id} style={{ ...s.jobCard, borderTop: `3px solid ${meta.color}` }}>
                    {/* card header */}
                    <div style={s.jobCardHead}>
                      <span style={{ ...s.statusBadge, color: meta.color, background: meta.bg, border: `1px solid ${meta.border}` }}>
                        {meta.label}
                      </span>
                      {pageStatus === 'published' && job.page_id && (
                        <a href={`${API_BASE}/p/${job.page_id}`} target="_blank" rel="noreferrer" style={s.viewLink}>
                          {t('crmViewPage')}
                        </a>
                      )}
                    </div>

                    {/* title */}
                    <h3 style={s.jobTitle}>{job.title}</h3>

                    {/* meta info */}
                    <div style={s.jobMeta}>
                      {job.company && <span style={s.metaChip}>🏢 {job.company}</span>}
                      {job.location && <span style={s.metaChip}>📍 {job.location}</span>}
                      {job.salary_range && <span style={s.metaChip}>💰 {job.salary_range}</span>}
                    </div>

                    {/* progress steps */}
                    <div style={s.stepsRow}>
                      {STEPS.map((label, idx) => {
                        const done = step > idx;
                        const active = step === idx + 1;
                        return (
                          <div key={label} style={s.stepItem}>
                            <div style={{
                              ...s.stepDot,
                              background: done || active ? meta.color : '#e5e7eb',
                              boxShadow: active ? `0 0 0 3px ${meta.color}33` : 'none',
                            }}>
                              {done ? '✓' : idx + 1}
                            </div>
                            {idx < STEPS.length - 1 && (
                              <div style={{ ...s.stepLine, background: done ? meta.color : '#e5e7eb' }} />
                            )}
                            <span style={{ ...s.stepLabel, color: done || active ? '#1a1a2e' : '#aaa', fontWeight: active ? 700 : 400 }}>
                              {label}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* footer */}
                    <div style={s.jobFooter}>
                      <span style={s.jobDate}>{t('crmSubmitDate')} {new Date(job.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* APPLICANTS SECTION */}
            <div style={{ marginTop: '40px' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px', marginBottom:'16px' }}>
                <h2 style={s.sectionTitle}>Applicants ({candidates.length})</h2>
                <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                  <input
                    placeholder="Search name or email…"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ padding:'7px 12px', border:'1px solid #e5e7eb', borderRadius:'8px', fontSize:'13px', outline:'none', minWidth:'180px' }}
                  />
                  <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} style={{ padding:'7px 12px', border:'1px solid #e5e7eb', borderRadius:'8px', fontSize:'13px', outline:'none', background:'#fff' }}>
                    <option value="">All stages</option>
                    {STAGES.map((st) => <option key={st} value={st}>{st.charAt(0).toUpperCase() + st.slice(1)}</option>)}
                  </select>
                </div>
              </div>

              {candidatesLoading ? (
                <div style={{ background:'#fff', borderRadius:'12px', padding:'40px', textAlign:'center', boxShadow:'0 2px 10px rgba(0,0,0,0.06)' }}>
                  <div style={s.spinner} /><p style={{ color:'#888', marginTop:'12px', fontSize:'14px' }}>Loading applicants…</p>
                </div>
              ) : filteredCandidates.length === 0 ? (
                <div style={{ background:'#fff', borderRadius:'12px', padding:'48px', textAlign:'center', boxShadow:'0 2px 10px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize:'36px', marginBottom:'12px' }}>👤</div>
                  <p style={{ color:'#888', fontSize:'14px', margin:0 }}>No applicants yet. They will appear here once someone submits a form on your landing page.</p>
                </div>
              ) : (
                <div style={{ background:'#fff', borderRadius:'12px', boxShadow:'0 2px 10px rgba(0,0,0,0.06)', overflow:'hidden' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
                    <thead>
                      <tr style={{ background:'#f8fafc', borderBottom:'1px solid #e5e7eb' }}>
                        {['Name','Email','Phone','Stage','Applied','CV','Details'].map((h) => (
                          <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontWeight:700, color:'#555', fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.5px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCandidates.map((c, i) => {
                        const sc = STAGE_COLORS[c.stage] || STAGE_COLORS.applied;
                        const raw = c.raw_data || {};
                        const cvUrl = raw.cv_url || Object.values(raw).find((v) => typeof v === 'string' && v.startsWith('/uploads/'));
                        return (
                          <tr key={c.id} style={{ borderBottom: i < filteredCandidates.length - 1 ? '1px solid #f0f0f0' : 'none', transition:'background .15s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#fafafa'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                            <td style={{ padding:'12px 14px', fontWeight:600, color:'#1a1a2e' }}>{c.full_name || '—'}</td>
                            <td style={{ padding:'12px 14px', color:'#555' }}>{c.email || '—'}</td>
                            <td style={{ padding:'12px 14px', color:'#555' }}>{c.phone || '—'}</td>
                            <td style={{ padding:'12px 14px' }}>
                              <span style={{ padding:'3px 10px', borderRadius:'20px', background:sc.bg, color:sc.color, fontWeight:700, fontSize:'11px', textTransform:'capitalize' }}>{c.stage}</span>
                            </td>
                            <td style={{ padding:'12px 14px', color:'#888' }}>{new Date(c.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}</td>
                            <td style={{ padding:'12px 14px' }}>
                              {cvUrl
                                ? <a href={`${API_BASE}${cvUrl}`} target="_blank" rel="noreferrer" style={{ color:'#5b6af0', fontWeight:600, fontSize:'12px', textDecoration:'none' }}>📎 Download</a>
                                : <span style={{ color:'#ccc', fontSize:'12px' }}>—</span>}
                            </td>
                            <td style={{ padding:'12px 14px' }}>
                              <button onClick={() => setSelectedCandidate(c)} style={{ padding:'5px 12px', background:'#eef2ff', color:'#5b6af0', border:'none', borderRadius:'6px', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {selectedCandidate && (
        <CandidateModal
          candidate={selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
          onStageChange={handleStageChange}
        />
      )}
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f4f5f7', fontFamily: 'Inter, sans-serif' },
  nav: {
    background: '#1a1a2e', color: '#fff', padding: '0 32px',
    height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    position: 'sticky', top: 0, zIndex: 100,
  },
  logo: { fontWeight: 800, fontSize: '16px', letterSpacing: '0.05em', cursor: 'pointer' },
  navR: { display: 'flex', alignItems: 'center', gap: '20px' },
  navLink: { fontSize: '13px', color: '#aaa', cursor: 'pointer', transition: 'color .2s' },
  logoutBtn: {
    background: 'transparent', border: '1px solid #444', color: '#ccc',
    padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
  },
  shell: { maxWidth: '1100px', margin: '0 auto', padding: '36px 24px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' },
  h1: { fontSize: '26px', fontWeight: 800, color: '#1a1a2e', margin: 0 },
  subtitle: { fontSize: '13px', color: '#888', margin: '4px 0 0' },
  ctaBtn: {
    background: '#1a1a2e', color: '#c6ff3e', border: 'none',
    padding: '10px 20px', borderRadius: '8px', fontSize: '13px',
    fontWeight: 700, cursor: 'pointer', letterSpacing: '0.03em',
  },

  loadingCard: {
    background: '#fff', borderRadius: '16px', padding: '60px',
    textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  spinner: {
    width: '32px', height: '32px', border: '3px solid #e5e7eb',
    borderTop: '3px solid #5b6af0', borderRadius: '50%',
    animation: 'spin 0.8s linear infinite', margin: '0 auto',
  },

  emptyCard: {
    background: '#fff', borderRadius: '16px', padding: '72px 40px',
    textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: { fontSize: '20px', fontWeight: 700, color: '#1a1a2e', marginBottom: '8px' },
  emptyText: { fontSize: '14px', color: '#888', marginBottom: '24px' },

  topGrid: {
    display: 'grid', gridTemplateColumns: '1fr auto',
    gap: '20px', marginBottom: '32px', alignItems: 'start',
  },
  statsCol: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' },
  statCard: {
    borderRadius: '12px', padding: '18px 20px',
    display: 'flex', flexDirection: 'column', gap: '4px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },
  statVal: { fontSize: '32px', fontWeight: 800, lineHeight: 1 },
  statLabel: { fontSize: '12px', color: '#666', fontWeight: 500, marginTop: '2px' },

  chartCard: {
    background: '#fff', borderRadius: '16px', padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)', textAlign: 'center',
    minWidth: '200px',
  },
  chartTitle: { fontSize: '12px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' },
  legend: { display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '16px', alignItems: 'flex-start' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '8px' },
  legendDot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  legendLabel: { fontSize: '12px', color: '#555' },

  sectionTitle: { fontSize: '17px', fontWeight: 700, color: '#1a1a2e', margin: '0 0 16px' },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '18px' },

  jobCard: {
    background: '#fff', borderRadius: '12px',
    padding: '20px 22px', boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
    display: 'flex', flexDirection: 'column', gap: '12px',
  },
  jobCardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  statusBadge: {
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px',
    textTransform: 'uppercase', borderRadius: '20px', padding: '4px 12px',
  },
  viewLink: {
    fontSize: '12px', fontWeight: 600, color: '#5b6af0',
    textDecoration: 'none', background: '#eef2ff',
    padding: '4px 10px', borderRadius: '6px',
  },
  jobTitle: { fontSize: '16px', fontWeight: 700, color: '#1a1a2e', margin: 0 },
  jobMeta: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  metaChip: {
    fontSize: '12px', color: '#555', background: '#f4f5f7',
    padding: '3px 10px', borderRadius: '20px', border: '1px solid #e5e7eb',
  },

  stepsRow: { display: 'flex', alignItems: 'flex-start', gap: 0, marginTop: '4px' },
  stepItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, position: 'relative' },
  stepDot: {
    width: '26px', height: '26px', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '11px', fontWeight: 700, color: '#fff', zIndex: 1, position: 'relative',
  },
  stepLine: {
    position: 'absolute', top: '13px', left: '50%', width: '100%', height: '2px', zIndex: 0,
  },
  stepLabel: { fontSize: '10px', textAlign: 'center', marginTop: '5px', lineHeight: 1.3 },

  jobFooter: { borderTop: '1px solid #f0f0f0', paddingTop: '10px' },
  jobDate: { fontSize: '11px', color: '#aaa' },
};
